<script setup>
import {computed} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useFieldsetsStore} from '@/stores/fieldsets'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useViewStore} from '@/stores/view'
import {useAppStore} from '@/stores/appStore'

const props = defineProps({
    viewId: String,
})

const appStore = useAppStore()
const viewStore = useViewStore(props.viewId)
const componentsStore = useComponentsStore()
const fieldsetsStore = useFieldsetsStore()
const componentStructureStore = useComponentStructureStore()

const tableName = computed(() => viewStore.tableName)
const activeColumns = computed(() => viewStore.columns || [])
const componentId = computed(() => tableName.value ? componentsStore.componentIds[tableName.value] : undefined)

const fieldsets = computed(() => {
    if (!componentId.value) return []
    return fieldsetsStore.items.filter(o => o.componentId === componentId.value)
})

const allColumns = computed(() => {
    if (!componentId.value) return []
    return componentStructureStore.get(componentId.value)
})

const columns = computed(() => {
    if (!componentId.value) return []
    let objs = componentStructureStore.get(componentId.value)

    // Remove devOnly columns.
    if (!appStore.betaMode) {
        objs = objs.filter(o => !o.devOnly)
    }

    objs = objs.filter(o => o.type !== 'hidden' && o.type !== 'fieldset')

    return objs
})

const getFieldsetColumns = (fieldsetId) => {
    return allColumns.value.filter(o => o.fieldsetId === fieldsetId)
}

const toggleColumnVisibility = (columnId) => {
    // Create a copy of the columns array.
    let currentColumns = JSON.parse(JSON.stringify(activeColumns.value))
    let i = currentColumns.indexOf(columnId)

    // Append or remove the item to/from it.
    // If appending a column this will add the new column onto the end of the array
    // but, we need the ids to be listed in the display order.
    i === -1
        ? currentColumns.push(columnId)
        : currentColumns.splice(i, 1)

    // So we must now recreate the columns array by the order of the component's fields array.
    let viewColumns = []
    columns.value.forEach((field) => {
        if (currentColumns.indexOf(field.id) > -1) {
            viewColumns.push(field.id)
        }
    })

    viewStore.setColumns(viewColumns)
}
</script>

<template>
    <div>
        <div v-for="fieldset in fieldsets" :key="fieldset.id" class="mb-3">
            <h6 class="m-3">{{ fieldset.legend }}</h6>
            <div class="list-group list-group-flush">
                <a v-for="column in getFieldsetColumns(fieldset.id)" :key="column.id"
                   class="list-group-item p-1 d-flex align-items-center"
                   @click="toggleColumnVisibility(column.id)">
                    <i class="me-1 bi-check-lg" :class="{'invisible': activeColumns.indexOf(column.id) === -1}"/>
                    {{ column.title }}
                </a>
            </div>
        </div>

        <h6>&nbsp;</h6>
        <div class="list-group list-group-flush">
            <a v-for="column in getFieldsetColumns(0)" :key="column.id"
               class="list-group-item p-1 d-flex align-items-center"
               @click="toggleColumnVisibility(column.id)">
                <i class="me-1 bi-check-lg" :class="{'invisible': activeColumns.indexOf(column.id) === -1}"/>
                {{ column.title }}
            </a>
        </div>
    </div>
</template>